import { type MouseEvent, useEffect, useRef, useState } from "react";
import ContactForm from "./ContactForm";
import { articleSources } from "./articleSources";
import logoSmall from "./assets/logo-varnos-128.webp";
import logoLarge from "./assets/logo-varnos-640.webp";
import { authorTexts } from "./authorTexts";
import {
  type Artwork,
  type Category,
  type MediaAsset,
  artworkCover,
  artworkMedia,
  localized,
  mediaAssets,
  projects,
  publications,
  publishedArtworks,
  vjDruskaArchiveMedia,
  vjDruskaArchivePdfUrl,
} from "./catalog";
import { type Locale, messages } from "./i18n";
import { updatePageSeo } from "./seo";

type Section = "home" | "about" | "works" | "projects" | "articles" | "contact";
const sections: Section[] = [
  "home",
  "about",
  "works",
  "projects",
  "articles",
  "contact",
];
const categories: Category[] = [
  "painting",
  "ceramics",
  "drawings",
  "fluxus",
  "other",
];
const mediaById = new Map(mediaAssets.map((item) => [item.id, item]));
const pdfById = new Map(articleSources.map((item) => [item.id, item]));
function route(pathname = window.location.pathname) {
  let path = pathname;
  if (/^\/works\/burtu-miskas\/?$/.test(path)) {
    path = "/projects/burtu-miskas/";
    if (typeof window !== "undefined")
      window.history.replaceState(null, "", path);
  }
  const parts = path
    .replace(/\/index\.html$/, "/")
    .split("/")
    .filter(Boolean);
  return {
    path,
    section: sections.includes(parts[0] as Section)
      ? (parts[0] as Section)
      : "home",
    slug: parts[1],
  };
}
function initialLocale(): Locale {
  try {
    return localStorage.getItem("am-locale") === "en" ? "en" : "lt";
  } catch {
    return "lt";
  }
}

function MediaVisual({
  url,
  alt,
  locale,
  loading,
}: { url?: string; alt: string; locale: Locale; loading?: "eager" | "lazy" }) {
  if (url)
    return <img src={url} alt={alt} loading={loading} decoding="async" />;
  return (
    <span
      className="media-placeholder"
      role="img"
      aria-label={`${alt ? `${alt}. ` : ""}${messages[locale].imageUnavailable}`}
    >
      <span>{messages[locale].imageUnavailable}</span>
    </span>
  );
}

export default function App({
  initialPath,
  initialLanguage,
}: { initialPath?: string; initialLanguage?: Locale } = {}) {
  const [locale, setLocale] = useState<Locale>(
    () => initialLanguage ?? initialLocale(),
  );
  const [location, setLocation] = useState(() => route(initialPath));
  const [category, setCategory] = useState<Category | "all">("all");
  const [viewer, setViewer] = useState<{
    artwork: Artwork;
    mediaIndex: number;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const viewerOpen = viewer !== null;
  const opener = useRef<HTMLElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const t = messages[locale];
  const page = location.section;
  const currentArtwork =
    page === "works"
      ? publishedArtworks.find((item) => item.slug === location.slug)
      : undefined;
  const project =
    page === "projects"
      ? projects.find((item) => item.slug === location.slug)
      : undefined;
  const publication =
    page === "articles"
      ? publications.find((item) => item.slug === location.slug)
      : undefined;
  const publicationHasScans = publication?.pdfIds.some(
    (id) => pdfById.get(id)?.pageUrl,
  );
  const publicationSummary =
    publication && "summary" in publication ? publication.summary : undefined;
  const publicationPreview =
    publication && "preview" in publication ? publication.preview : undefined;
  const visibleWorks =
    category === "all"
      ? publishedArtworks
      : publishedArtworks.filter((item) => item.category === category);
  const selectedWorks = [
    ...categories.flatMap((kind) =>
      publishedArtworks
        .filter((item) => item.category === kind)
        .slice(0, kind === "ceramics" || kind === "painting" ? 2 : 1),
    ),
  ].slice(0, 8);
  const forestProject = projects.find((item) => item.slug === "burtu-miskas");
  const firstForest = forestProject
    ? mediaById.get(forestProject.mediaIds[0])
    : undefined;

  useEffect(() => {
    updatePageSeo(location.path, locale);
    try {
      localStorage.setItem("am-locale", locale);
    } catch {
      /* optional */
    }
  }, [locale, location.path]);

  useEffect(() => {
    const onPop = () => {
      setLocation(route());
      setViewer(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!viewerOpen) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setViewer(null);
      if (event.key === "ArrowRight" || event.key === "ArrowLeft")
        setViewer(
          (current) =>
            current && {
              ...current,
              mediaIndex:
                (current.mediaIndex +
                  (event.key === "ArrowRight" ? 1 : -1) +
                  current.artwork.mediaIds.length) %
                current.artwork.mediaIds.length,
            },
        );
      if (event.key === "Tab") {
        const nodes =
          document.querySelectorAll<HTMLElement>(".lightbox button");
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKey);
      opener.current?.focus();
    };
  }, [viewerOpen]);

  function navigate(path: string, event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    window.history.pushState(null, "", path);
    setLocation(route());
    setMenuOpen(false);
    setViewer(null);
    window.scrollTo(0, 0);
  }
  function link(path: string, label: string, className?: string) {
    return (
      <a
        className={className}
        href={path}
        onClick={(event) => navigate(path, event)}
      >
        {label}
      </a>
    );
  }
  function openViewer(
    artwork: Artwork,
    mediaIndex: number,
    event: MouseEvent<HTMLElement>,
  ) {
    opener.current = event.currentTarget;
    setViewer({ artwork, mediaIndex });
  }
  const activeArtwork = viewer?.artwork;
  const activeMedia = activeArtwork && artworkMedia(activeArtwork);
  const shownMedia = activeMedia?.[viewer?.mediaIndex || 0];

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">
        {t.skip}
      </a>
      <header className="site-header">
        <div className="container header-inner">
          <a
            className="brand"
            href="/"
            aria-label={t.title}
            onClick={(event) => navigate("/", event)}
          >
            <img src={logoSmall} alt="" width="128" height="116" />
            <span>{t.title}</span>
          </a>
          <button
            type="button"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="main-nav"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? t.closeMenu : t.menu}
          </button>
          <div className={`header-right ${menuOpen ? "is-open" : ""}`}>
            <nav id="main-nav" className="site-nav" aria-label={t.navigation}>
              {sections.map((section) => (
                <a
                  key={section}
                  href={section === "home" ? "/" : `/${section}/`}
                  aria-current={page === section ? "page" : undefined}
                  onClick={(event) =>
                    navigate(section === "home" ? "/" : `/${section}/`, event)
                  }
                >
                  {t[section]}
                </a>
              ))}
            </nav>
            <div className="language-switch" aria-label={t.language}>
              <button
                type="button"
                lang="lt"
                aria-pressed={locale === "lt"}
                onClick={() => setLocale("lt")}
              >
                LT
              </button>
              <span aria-hidden="true">/</span>
              <button
                type="button"
                lang="en"
                aria-pressed={locale === "en"}
                onClick={() => setLocale("en")}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </header>
      <main id="main">
        {page === "home" && (
          <>
            <section className="hero container">
              <div className="hero-copy">
                <p className="eyebrow">{t.practice}</p>
                <h1>{t.title}</h1>
                <p>{t.intro}</p>
                {link("/works/", t.allWorks, "text-link")}
              </div>
              {firstForest && (
                <a
                  className="hero-image"
                  href="/projects/burtu-miskas/"
                  onClick={(event) =>
                    navigate("/projects/burtu-miskas/", event)
                  }
                  aria-label={localized(forestProject?.title, locale)}
                >
                  <MediaVisual
                    url={firstForest.previewUrl}
                    alt={
                      localized(forestProject?.title, locale) ||
                      firstForest.alt[locale] ||
                      ""
                    }
                    locale={locale}
                  />
                </a>
              )}
            </section>
            <section className="container section-block">
              <div className="section-head">
                <div>
                  <p className="eyebrow">01</p>
                  <h2>{t.selected}</h2>
                </div>
                {link("/works/", t.allWorks, "text-link")}
              </div>
              <ArtworkGrid
                items={selectedWorks}
                locale={locale}
                navigate={navigate}
                className="art-grid selected-grid"
              />
            </section>
            <section className="container home-editorial">
              <div>
                <p className="eyebrow">02</p>
                <h2>{t.about}</h2>
                <p>{t.aboutOne}</p>
                {link("/about/", t.about, "text-link")}
              </div>
              <div>
                <p className="eyebrow">03</p>
                <h2>{t.seeProjects}</h2>
                {projects.map((item) => (
                  <p key={item.slug}>
                    {link(
                      `/projects/${item.slug}/`,
                      localized(item.title, locale) || "",
                    )}
                  </p>
                ))}
              </div>
            </section>
            <section className="container section-block compact-section">
              <div className="section-head">
                <div>
                  <p className="eyebrow">04</p>
                  <h2>{t.publications}</h2>
                </div>
                {link("/articles/", t.articles, "text-link")}
              </div>
              {publications.map((item) => (
                <p key={item.slug} className="publication-link">
                  {link(
                    `/articles/${item.slug}/`,
                    localized(item.title, locale) || "",
                  )}
                </p>
              ))}
            </section>
          </>
        )}
        {page === "works" &&
          (location.slug ? (
            currentArtwork ? (
              <ArtworkDetailPage
                artwork={currentArtwork}
                locale={locale}
                navigate={navigate}
                openViewer={openViewer}
              />
            ) : (
              <div className="container page-content">
                <PageHeading eyebrow="01" title={t.works} lead={t.notFound} />
                <p>{link("/works/", `← ${t.back}`, "text-link")}</p>
              </div>
            )
          ) : (
            <WorksPage
              items={visibleWorks}
              category={category}
              setCategory={setCategory}
              locale={locale}
              navigate={navigate}
            />
          ))}
        {page === "projects" && (
          <div className="container page-content">
            <PageHeading
              eyebrow="02"
              title={
                project ? localized(project.title, locale) || "" : t.projects
              }
              lead={
                project
                  ? localized(project.intro, locale) || ""
                  : t.projectsLead
              }
            />
            {project ? (
              <>
                <p>{link("/projects/", `← ${t.back}`, "text-link")}</p>
                {"body" in project && (
                  <div className="prose project-body">
                    <p>{localized(project.body, locale)}</p>
                  </div>
                )}
                {authorTexts[project.slug] && (
                  <section
                    className="prose project-body"
                    aria-label={t.authorText}
                  >
                    <h2>{t.authorText}</h2>
                    <div lang="lt">
                      {authorTexts[project.slug].map((paragraph, index) => (
                        <p key={`${project.slug}-${index}`}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                )}
                {project.mediaIds.length > 0 && (
                  <div className="project-gallery">
                    {project.mediaIds.map((id, index) => {
                      const media = mediaById.get(id);
                      const fullUrl = media?.fullUrl || media?.previewUrl;
                      return (
                        media && (
                          <figure key={id}>
                            {fullUrl ? (
                              <a
                                href={fullUrl}
                                aria-label={`${t.openFullSize}: ${localized(project.title, locale) || t.projects}, ${t.image.toLowerCase()} ${index + 1}`}
                              >
                                <MediaVisual
                                  url={media.previewUrl}
                                  alt={`${localized(project.title, locale)} — ${t.image.toLowerCase()} ${index + 1}`}
                                  locale={locale}
                                  loading="lazy"
                                />
                              </a>
                            ) : (
                              <MediaVisual
                                url={media.previewUrl}
                                alt={`${localized(project.title, locale)} — ${t.image.toLowerCase()} ${index + 1}`}
                                locale={locale}
                                loading="lazy"
                              />
                            )}
                            <figcaption>
                              {localized(project.title, locale)} · {t.image}{" "}
                              {index + 1}
                            </figcaption>
                          </figure>
                        )
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="editorial-list">
                {projects.map((item, index) => {
                  const media = mediaById.get(item.mediaIds[0]);
                  return (
                    <article
                      key={item.slug}
                      className={`editorial-row${media ? "" : " editorial-row--text-only"}`}
                    >
                      <span className="eyebrow">0{index + 1}</span>
                      <div>
                        <h2>
                          {link(
                            `/projects/${item.slug}/`,
                            localized(item.title, locale) || "",
                          )}
                        </h2>
                        <p>{localized(item.intro, locale)}</p>
                      </div>
                      {media && (
                        <MediaVisual
                          url={media.previewUrl}
                          alt=""
                          locale={locale}
                          loading="lazy"
                        />
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {page === "articles" && (
          <div className="container page-content">
            <PageHeading
              eyebrow="03"
              title={
                publication
                  ? localized(publication.title, locale) || ""
                  : t.articles
              }
              lead={publication ? "" : t.articlesLead}
            />
            {publication ? (
              <>
                <p>{link("/articles/", `← ${t.back}`, "text-link")}</p>
                <p className="contact-help">{publication.credit}</p>
                {(publicationSummary || publicationPreview) && (
                  <div className="publication-intro">
                    {publicationPreview && (
                      <figure className="publication-preview">
                        <a
                          href={
                            pdfById.get(publicationPreview.sourceId)?.pageUrl
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={publicationPreview.url}
                            alt={localized(publicationPreview.alt, locale)}
                            width={publicationPreview.width}
                            height={publicationPreview.height}
                            decoding="async"
                          />
                        </a>
                        <figcaption>
                          {localized(publicationPreview.caption, locale)} ·{" "}
                          {t.openFullSize} ↗
                        </figcaption>
                      </figure>
                    )}
                    {publicationSummary && (
                      <section
                        className="publication-summary"
                        aria-labelledby="publication-summary-heading"
                      >
                        <h2 id="publication-summary-heading">
                          {publication && "summaryTitle" in publication
                            ? localized(publication.summaryTitle, locale)
                            : t.articleSummary}
                        </h2>
                        <p>{localized(publicationSummary, locale)}</p>
                      </section>
                    )}
                  </div>
                )}
                {publicationSummary && (
                  <h2 className="publication-originals-heading">
                    {t.articleOriginalPages}
                  </h2>
                )}
                <p className="prose">
                  {"originalsNote" in publication
                    ? localized(publication.originalsNote, locale)
                    : publicationHasScans
                      ? t.articleScanNote
                      : t.articleIssueNote}
                </p>
                {publicationHasScans && (
                  <div className="publication-pages">
                    {publication.pdfIds.map((id) => {
                      const source = pdfById.get(id);
                      return source?.pageUrl ? (
                        <figure key={id}>
                          <a
                            href={source.pageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={source.pageUrl}
                              alt={`${localized(publication.title, locale)} — ${t.page} ${source.page}`}
                              loading="lazy"
                            />
                          </a>
                          <figcaption>
                            {t.page} {source.page} · {t.openFullSize} ↗
                          </figcaption>
                        </figure>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="documents">
                  {publication.pdfIds.map((id, index) => {
                    const pdf = pdfById.get(id);
                    return (
                      pdf?.mediaUrl && (
                        <a
                          key={id}
                          href={pdf.mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {(typeof pdf.label === "string"
                            ? pdf.label
                            : localized(pdf.label, locale)) ||
                            `${t.sourcePdf} ${index + 1}`}{" "}
                          ↗
                        </a>
                      )
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="editorial-list">
                {publications.map((item, index) => (
                  <article key={item.slug} className="editorial-row">
                    <span className="eyebrow">0{index + 1}</span>
                    <div>
                      <h2>
                        {link(
                          `/articles/${item.slug}/`,
                          localized(item.title, locale) || "",
                        )}
                      </h2>
                      <p>{item.credit}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
        {page === "about" && (
          <div className="container page-content">
            <PageHeading eyebrow="04" title={t.about} lead={t.aboutLead} />
            <div className="prose">
              <p>{t.aboutOne}</p>
              <p>{t.aboutTwo}</p>
              <section>
                <h2>{t.education}</h2>
                <p>{t.educationText}</p>
              </section>
              <section>
                <h2>{t.activity}</h2>
                <p>{t.activityText}</p>
              </section>
              <section id="vj-druska" aria-labelledby="vj-druska-heading">
                <h2 id="vj-druska-heading">{t.vjDruska}</h2>
                <p>{t.vjDruskaText}</p>
                <div className="project-gallery">
                  {vjDruskaArchiveMedia.map((media, index) => (
                    <figure key={media.id}>
                      <a
                        href={media.fullUrl}
                        aria-label={`${t.openFullSize}: ${t.vjDruskaImage}, ${index + 1}`}
                      >
                        <MediaVisual
                          url={media.previewUrl}
                          alt={`${t.vjDruskaImage}, ${index + 1}`}
                          locale={locale}
                          loading="lazy"
                        />
                      </a>
                    </figure>
                  ))}
                </div>
                {vjDruskaArchivePdfUrl && (
                  <>
                    <p>
                      <a className="text-link" href={vjDruskaArchivePdfUrl}>
                        {t.vjDruskaDocument}
                      </a>
                    </p>
                    <p>{t.vjDruskaDocumentNote}</p>
                  </>
                )}
              </section>
            </div>
          </div>
        )}
        {page === "contact" && (
          <div className="container page-content">
            <PageHeading eyebrow="05" title={t.contact} lead={t.contactLead} />
            <div className="contact-layout">
              <aside className="contact-intro">
                <img
                  className="contact-logo"
                  src={logoLarge}
                  alt={t.logoAlt}
                  width="640"
                  height="583"
                  decoding="async"
                />
                <p id="contact-note">{t.contactNote}</p>
              </aside>
              <ContactForm locale={locale} />
            </div>
          </div>
        )}
      </main>
      <footer className="site-footer">
        <div className="container footer-inner">
          <span>{t.title}</span>
          <span>{t.footer}</span>
          {link("/contact/", t.contact)}
        </div>
      </footer>
      {viewer && activeArtwork && shownMedia && (
        <dialog
          open
          className="lightbox"
          aria-modal="true"
          aria-label={t.viewer}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setViewer(null);
          }}
        >
          <div className="lightbox-top">
            <span>
              {viewer.mediaIndex + 1} / {activeArtwork.mediaIds.length}
            </span>
            <button
              ref={closeButton}
              type="button"
              onClick={() => setViewer(null)}
            >
              {t.close} ×
            </button>
          </div>
          <div className="lightbox-stage">
            <button
              type="button"
              aria-label={t.previous}
              onClick={() =>
                setViewer({
                  ...viewer,
                  mediaIndex:
                    (viewer.mediaIndex - 1 + activeArtwork.mediaIds.length) %
                    activeArtwork.mediaIds.length,
                })
              }
            >
              ←
            </button>
            <MediaVisual
              url={shownMedia.fullUrl}
              alt={
                localized(activeArtwork.title, locale) ||
                shownMedia.alt[locale] ||
                ""
              }
              locale={locale}
            />
            <button
              type="button"
              aria-label={t.next}
              onClick={() =>
                setViewer({
                  ...viewer,
                  mediaIndex:
                    (viewer.mediaIndex + 1) % activeArtwork.mediaIds.length,
                })
              }
            >
              →
            </button>
          </div>
          <div className="lightbox-caption">
            {localized(activeArtwork.title, locale) && (
              <strong>{localized(activeArtwork.title, locale)}</strong>
            )}
            {[
              activeArtwork.year,
              localized(activeArtwork.technique, locale),
              localized(activeArtwork.materials, locale),
              localized(activeArtwork.dimensions, locale),
              activeArtwork.firingTemperature,
              localized(activeArtwork.owner, locale),
            ]
              .filter(Boolean)
              .join(" · ")}
            {shownMedia.originalUrl && (
              <p>
                <SketchDownloads media={shownMedia} locale={locale} />
              </p>
            )}
            {activeMedia && activeMedia.length > 1 && (
              <div>
                {activeMedia.map((media, index) => (
                  <button
                    key={media.id}
                    type="button"
                    aria-pressed={viewer.mediaIndex === index}
                    onClick={() => setViewer({ ...viewer, mediaIndex: index })}
                  >
                    {t.image} {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </dialog>
      )}
    </div>
  );
}

function PageHeading({
  eyebrow,
  title,
  lead,
}: { eyebrow: string; title: string; lead: string }) {
  return (
    <header className="page-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      {lead && <p>{lead}</p>}
    </header>
  );
}

type Navigate = (path: string, event: MouseEvent<HTMLAnchorElement>) => void;
function ArtworkFilter({
  category,
  setCategory,
  locale,
}: {
  category: Category | "all";
  setCategory: (category: Category | "all") => void;
  locale: Locale;
}) {
  const t = messages[locale];
  return (
    <fieldset className="filters">
      <legend className="visually-hidden">{t.works}</legend>
      {(
        [
          "all",
          ...categories.filter((kind) =>
            publishedArtworks.some((work) => work.category === kind),
          ),
        ] as (Category | "all")[]
      ).map((kind) => (
        <button
          key={kind}
          type="button"
          aria-pressed={category === kind}
          onClick={() => setCategory(kind)}
        >
          {t[kind]}
        </button>
      ))}
    </fieldset>
  );
}
function ArtworkCard({
  item,
  locale,
  navigate,
}: { item: Artwork; locale: Locale; navigate: Navigate }) {
  const media = artworkCover(item);
  if (!media) return null;
  const title = localized(item.title, locale) || "";
  return (
    <figure className="art-item">
      <a
        className="art-image-button"
        href={`/works/${item.slug}/`}
        onClick={(event) => navigate(`/works/${item.slug}/`, event)}
        aria-label={title}
      >
        <MediaVisual
          url={media.previewUrl}
          alt={title}
          locale={locale}
          loading="lazy"
        />
      </a>
      <figcaption>
        <a
          href={`/works/${item.slug}/`}
          onClick={(event) => navigate(`/works/${item.slug}/`, event)}
        >
          {title}
        </a>
      </figcaption>
      {(item.year || item.technique) && (
        <p className="art-meta">
          {[item.year, localized(item.technique, locale)]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
    </figure>
  );
}
function ArtworkGrid({
  items,
  locale,
  navigate,
  className = "art-grid",
}: {
  items: Artwork[];
  locale: Locale;
  navigate: Navigate;
  className?: string;
}) {
  return (
    <div className={className}>
      {items.map((item) => (
        <ArtworkCard
          key={item.id}
          item={item}
          locale={locale}
          navigate={navigate}
        />
      ))}
    </div>
  );
}
function WorksPage({
  items,
  category,
  setCategory,
  locale,
  navigate,
}: {
  items: Artwork[];
  category: Category | "all";
  setCategory: (category: Category | "all") => void;
  locale: Locale;
  navigate: Navigate;
}) {
  const t = messages[locale];
  return (
    <div className="container page-content">
      <PageHeading eyebrow="01" title={t.works} lead={t.worksLead} />
      <ArtworkFilter
        category={category}
        setCategory={setCategory}
        locale={locale}
      />
      <ArtworkGrid items={items} locale={locale} navigate={navigate} />
    </div>
  );
}
function SketchDownloads({
  media,
  locale,
}: { media: MediaAsset; locale: Locale }) {
  return (
    <>
      {media.pdfUrl && (
        <>
          <a href={media.pdfUrl} download>
            {locale === "lt" ? "PDF peržiūrai" : "Viewing PDF"}
          </a>
          {" · "}
        </>
      )}
      {media.originalUrl && (
        <a href={media.originalUrl} download>
          {media.pdfUrl
            ? locale === "lt"
              ? "Originalus PDF"
              : "Original PDF"
            : locale === "lt"
              ? "Originalus vaizdas"
              : "Original image"}
        </a>
      )}
    </>
  );
}
function ArtworkDetailPage({
  artwork,
  locale,
  navigate,
  openViewer,
}: {
  artwork: Artwork;
  locale: Locale;
  navigate: Navigate;
  openViewer: (
    artwork: Artwork,
    mediaIndex: number,
    event: MouseEvent<HTMLElement>,
  ) => void;
}) {
  const t = messages[locale];
  const media = artworkMedia(artwork);
  const index = publishedArtworks.findIndex((item) => item.id === artwork.id);
  const previous = publishedArtworks[index - 1];
  const next = publishedArtworks[index + 1];
  const title = localized(artwork.title, locale) || "";
  return (
    <div className="container page-content artwork-detail">
      <p>
        <a
          href="/works/"
          className="text-link"
          onClick={(event) => navigate("/works/", event)}
        >
          ← {t.back}
        </a>
      </p>
      <PageHeading eyebrow={t[artwork.category]} title={title} lead="" />
      {(artwork.year ||
        artwork.technique ||
        artwork.materials ||
        artwork.dimensions ||
        artwork.firingTemperature ||
        artwork.owner) && (
        <p className="artwork-facts">
          {[
            artwork.year,
            localized(artwork.technique, locale),
            localized(artwork.materials, locale),
            localized(artwork.dimensions, locale),
            artwork.firingTemperature,
            localized(artwork.owner, locale),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
      {artwork.description && (
        <div className="prose artwork-description">
          <p>{localized(artwork.description, locale)}</p>
          {artwork.exhibitionLink && (
            <p>
              <a
                className="text-link"
                href={artwork.exhibitionLink.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {localized(artwork.exhibitionLink.label, locale)}
              </a>
            </p>
          )}
        </div>
      )}
      <div className="artwork-media-grid">
        {media.map((item, mediaIndex) => (
          <button
            key={item.id}
            type="button"
            onClick={(event) => openViewer(artwork, mediaIndex, event)}
            aria-label={`${t.image} ${mediaIndex + 1}: ${title}`}
          >
            <MediaVisual
              url={item.previewUrl}
              alt={`${title}, ${t.image.toLowerCase()} ${mediaIndex + 1}`}
              locale={locale}
              loading={mediaIndex === 0 ? "eager" : "lazy"}
            />
          </button>
        ))}
      </div>
      <nav className="artwork-neighbors" aria-label={t.works}>
        {previous ? (
          <a
            href={`/works/${previous.slug}/`}
            onClick={(event) => navigate(`/works/${previous.slug}/`, event)}
          >
            ← {t.previous}: {localized(previous.title, locale)}
          </a>
        ) : (
          <span />
        )}
        {next && (
          <a
            href={`/works/${next.slug}/`}
            onClick={(event) => navigate(`/works/${next.slug}/`, event)}
          >
            {t.next}: {localized(next.title, locale)} →
          </a>
        )}
      </nav>
    </div>
  );
}
